import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Layers } from 'lucide-react'
import { Button, Divider, Tag } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { contextCompanyIdSelector } from '../../../redux/context/company/selectors'
import { filtersSelector } from '../../../redux/context/filters/selectors'
import { updateContextFilterAction, setPeriodGroupFilter } from '../../../redux/context/filters/actions'
import { AppDispatch } from '../../../redux/store'
import { ContractProduct } from '../../../types/contract/Contract'
import { useAuthority } from '../../../utils/Authorizable/authorize'
import CtxDimensionTreeSelect from '../../dimension/components/CtxDimensionTreeSelect'
import LucideIcon from '../../../shared/components/LucideIcon'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeDimensions = (value: any): string[] => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value.map((v: { value: string } | string) => (typeof v === 'object' ? v.value : v))
  }
  return [typeof value === 'object' ? value.value : value]
}

const ContextDimensionSelect: React.FC = () => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()
  const companyId = useSelector(contextCompanyIdSelector)
  const filters = useSelector(filtersSelector)
  const isPro = useAuthority({ product: ContractProduct.PRO })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [localValue, setLocalValue] = useState<any>(filters?.dimensions?.map(d => ({ value: d })) || [])

  useEffect(() => {
    setLocalValue(filters?.dimensions?.map(d => ({ value: d })) || [])
  }, [filters?.dimensions, companyId])

  const applyDimensions = useCallback(
    (dimensions: string[]) => {
      dispatch(setPeriodGroupFilter(undefined))
      dispatch(updateContextFilterAction({ dimensions }))
    },
    [dispatch]
  )

  const justClearedRef = useRef(false)

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      if (justClearedRef.current) {
        justClearedRef.current = false
        return
      }
      applyDimensions(normalizeDimensions(localValue))
    }
  }

  const handleClear = () => {
    justClearedRef.current = true
    setLocalValue([])
    applyDimensions([])
  }

  const hasDimensions = !!filters.dimensions?.length

  return (
    <CtxDimensionTreeSelect
      data-cy="context-dimension-select"
      className={hasDimensions ? 'active' : undefined}
      prefix={<LucideIcon icon={Layers} />}
      style={{ minWidth: 200, maxWidth: 500 }}
      value={localValue}
      onChange={(value: any) => {
        setLocalValue(value)
        const dimensions = normalizeDimensions(value)
        if (dimensions.length === 0) {
          applyDimensions([])
        }
      }}
      onOpenChange={handleOpenChange}
      treeCheckable={isPro}
      treeCheckStrictly
      labelInValue
      maxTagCount={3}
      maxTagPlaceholder={omittedValues => `+${omittedValues.length}`}
      tagRender={props => (
        <Tag
          style={{
            maxWidth: 120,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle'
          }}
        >
          {props.label}
        </Tag>
      )}
      placeholder={t('global:dimensions')}
      popupRender={menu => (
        <>
          {menu}
          <Divider style={{ margin: '4px 0' }} />
          <Button type="text" size="small" onClick={handleClear} style={{ width: '100%' }}>
            {t('periodSelect:clear')}
          </Button>
        </>
      )}
    />
  )
}

export default ContextDimensionSelect
